import { QueryClientProvider } from "@tanstack/react-query";
import { App as AntApp, ConfigProvider, Spin } from "antd";
import { createElement, lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import CorePage from "./app/CorePage";
import { canSee, ROUTES } from "./app/routes";
import { EmptyState } from "./components/Bits";
import { queryClient } from "./lib/queryClient";
import { signOut, useSession } from "./lib/session";
import LoginPage from "./pages/LoginPage";
import NotFoundPage from "./pages/NotFoundPage";

const PublicStatusPage = lazy(() => import("./pages/PublicStatusPage"));

/** Supervisor's antd theme (App.tsx there), unchanged: navy primary, brick links, sharp controls. */
const theme = {
  token: {
    colorPrimary: "#0D1245",
    colorLink: "#C0443E",
    colorText: "#0D1245",
    colorTextSecondary: "#5A5F78",
    colorBorder: "#E0E2EC",
    colorBorderSecondary: "#EBECF3",
    colorError: "#C0443E",
    colorWarning: "#B7791F",
    colorSuccess: "#15795B",
    fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 14,
    borderRadius: 6,
  },
  components: {
    Table: { headerBg: "#F1F2F8", headerColor: "#5A5F78", headerSplitColor: "#E0E2EC", borderColor: "#EBECF3", rowHoverBg: "#F1F2F8", cellPaddingBlock: 12 },
    Button: { borderRadius: 0, controlHeight: 36, fontWeight: 600, primaryShadow: "none", defaultShadow: "none" },
    Card: { borderRadiusLG: 16 },
    Form: { labelColor: "#0D1245" },
    Input: { colorBgContainer: "#FFFFFF", colorBorder: "#D2D5E4", borderRadius: 0, controlHeight: 36, fontSize: 14 },
    InputNumber: { colorBgContainer: "#FFFFFF", colorBorder: "#D2D5E4", borderRadius: 0, controlHeight: 36, fontSize: 14 },
    Select: { colorBgContainer: "#FFFFFF", colorBorder: "#D2D5E4", borderRadius: 0, controlHeight: 36, fontSize: 14 },
    DatePicker: { colorBgContainer: "#FFFFFF", colorBorder: "#D2D5E4", borderRadius: 0, controlHeight: 36 },
    Segmented: { borderRadius: 0, borderRadiusSM: 0, itemSelectedBg: "#FFFFFF", trackBg: "#E4E6F0" },
    Drawer: { colorBgElevated: "#FFFFFF" },
  },
};

function Splash() {
  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-4 bg-canvas">
      <span className="font-grotesk text-2xl font-bold tracking-tight text-ink">Pulse</span>
      <Spin />
    </div>
  );
}

function NoOrganization() {
  return (
    <div className="flex h-dvh items-center justify-center bg-canvas">
      <EmptyState
        title="You're not in an organization"
        body="You were removed from your last organization. Ask an owner to invite you again."
        action={
          <button className="btn-sharp btn-secondary" onClick={() => void signOut()}>
            Sign out
          </button>
        }
      />
    </div>
  );
}

function AppRoutes() {
  const status = useSession((s) => s.status);
  const profile = useSession((s) => s.profile);
  const location = useLocation();

  // Public status links work signed in or not, without waiting for the session.
  if (location.pathname.startsWith("/s/")) {
    return (
      <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/s/:token" element={<PublicStatusPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (status === "loading") return <Splash />;
  if (status !== "signedIn" || !profile) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }
  if (!profile.org) return <NoOrganization />;

  const allowed = ROUTES.filter((r) => canSee(r, profile.menu));
  return (
    <Routes>
      <Route path="/login" element={<Navigate to={(location.state as { from?: string } | null)?.from ?? "/"} replace />} />
      <Route element={<CorePage />}>
        {allowed.flatMap((r) => [
          <Route key={r.path} path={r.path} element={createElement(r.element)} />,
          ...(r.children ?? []).map((c) => <Route key={`${r.path}/${c.path}`} path={`${r.path}/${c.path}`} element={createElement(c.element)} />),
        ])}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <ConfigProvider theme={theme}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}
